import { Controller } from "@hotwired/stimulus"

// Converts Persian (۰-۹) and Arabic-Indic (٠-٩) digits to Western ones —
// used when a group has data-normalize-digits="true" (see otp_controller.js
// for the same conversion applied to typed/pasted OTP digits).
function toWesternDigits(value) {
  return String(value)
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
}

// Generic client-side form validation, driven entirely by markup — no
// per-form JS needed. Wrap a field with UI::ValidatedFieldComponent and
// it's checked here on submit; this deliberately never uses native HTML5
// validation (`required`, etc.), since a required input hidden behind
// custom markup (an upload tile, a picker trigger) can't be focused by the
// browser to show its own message.
//
//   <%= form_with ..., data: { controller: "form-validation",
//                               action: "submit->form-validation#validate " \
//                                       "input->form-validation#clearField " \
//                                       "change->form-validation#clearField" } do %>
//     <%= render UI::ValidatedFieldComponent.new(message: "...", required: true) do %>
//       <input type="text" name="...">
//     <% end %>
//   <% end %>
//
// Each UI::ValidatedFieldComponent wrapper (a "group") carries its rule as
// plain data attributes (data-required, data-pattern, data-normalize-digits)
// and is checked by finding the first input/select/textarea inside it —
// callers never add validation attributes to their own field markup. A
// group clears as soon as any input/change event bubbles up through it.
export default class extends Controller {
  static targets = ["group"]

  validate(event) {
    let firstInvalid = null

    this.groupTargets.forEach((group) => {
      if (!this._checkGroup(group) && !firstInvalid) firstInvalid = group
    })

    if (firstInvalid) {
      event.preventDefault()
      const field = this._fieldFor(firstInvalid)
      if (field && field.type !== "file" && field.type !== "hidden" && typeof field.focus === "function") {
        field.focus()
      } else {
        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  }

  /** Bound to input/change on the whole form — clears whichever group the event happened in. */
  clearField(event) {
    const group = event.target.closest('[data-form-validation-target="group"]')
    if (group) this._setInvalid(group, false)
  }

  _checkGroup(group) {
    const field = this._fieldFor(group)
    if (!field) return true

    const isFile = field.type === "file"
    const raw = isFile ? null : field.value.trim()
    const value = raw !== null && group.dataset.normalizeDigits === "true" ? toWesternDigits(raw) : raw

    let ok = true
    if (group.dataset.required === "true") {
      ok = isFile ? field.files.length > 0 : value.length > 0
    }
    if (ok && group.dataset.pattern && value) {
      ok = new RegExp(group.dataset.pattern).test(value)
    }

    this._setInvalid(group, !ok)
    return ok
  }

  _fieldFor(group) {
    return group.querySelector("input, select, textarea")
  }

  _setInvalid(group, invalid) {
    group.classList.toggle("field-invalid", invalid)
    const error = group.querySelector('[data-form-validation-target="error"]')
    if (error) error.classList.toggle("hidden", !invalid)
  }
}
